import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  from,
  InMemoryCache,
} from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { onError } from '@apollo/client/link/error';

const httpBatchLink = new BatchHttpLink({
  uri: '/graphql',
  batchMax: 20,
  batchInterval: 10,
});

const httpPriorityBatchLink = new BatchHttpLink({
  uri: '/graphql',
  batchMax: 5,
  batchInterval: 5,
});

const batchSplitterLink = ApolloLink.split(
  // if an operation's context is marked as priority, put it into a different batch
  // this allows something like user/farm context to return faster than expensive econ queries
  // but, keep in a small batch to still get some benefits of batching
  // when there are multiple priority queries
  (operation) => operation.getContext().priorityOp,
  httpPriorityBatchLink,
  httpBatchLink,
);

// Adds error logging
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path }) => {
      const locationsFormatted = (locations || [])
        .map(({ line, column }) => `Line: ${line}, Column: ${column}`)
        .join(', ');

      console.error(
        `[GraphQL error]: Message: ${message}, Locations: ${locationsFormatted}, Path: ${path}`,
      );
    });
  if (networkError) console.error(`[Network error]: ${networkError}`);
});

const getToken = () => {
  const csrfSelector = document.querySelector('meta[name="csrf-token"]');
  return csrfSelector && csrfSelector.getAttribute('content');
};

const authMiddleware = new ApolloLink((operation, forward) => {
  operation.setContext({
    headers: {
      'X-CSRF-Token': getToken(),
    },
  });

  return forward(operation);
});

const client = new ApolloClient({
  cache: new InMemoryCache({
    typePolicies: {
      /**
       * Set merge functions for the subfields of Farm and SectionGroup.
       *
       * NOTE: `{ merge: true }` is a shorthand for the basic merge function.
       */
      SectionGroup: {
        fields: {
          ecologicalSummary: { merge: true },
          economicSummary: { merge: true },
        },
      },
      Farm: {
        crops: { merge: true },
        fields: {
          ecologicalSummary: { merge: true },
          economicSummary: { merge: true },
          notes: {
            // when we re-query notes, just replace the array:
            merge(_existing, incoming) {
              return incoming;
            },
          },
        },
      },

      /**
       * We set `{ keyFields: false }` for these fields to disable
       * normalization. These fields should not be cached on their own, only
       * within a containing field with an `id`.
       */
      CropAggregate: { keyFields: false },
      YieldAggregate: { keyFields: false },
      EcologicalSummary: { keyFields: false },
      EconomicSummary: { keyFields: false },
      InputAggregate: { keyFields: false },
      OperationAggregate: { keyFields: false },
      RevenueStreamAggregate: { keyFields: false },
      ProratedCapex: { keyFields: false },

      // We disable normalization for TeamMember since a user has one id, but different permissions in different contexts
      TeamMember: { keyFields: false },
    },
    possibleTypes: {
      SectionsAggregate: ['Farm', 'SectionGroup'],
    },
  }),
  link: from([errorLink, authMiddleware, batchSplitterLink]),
});

const ApolloClientProvider = ({ children }) => {
  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default ApolloClientProvider;
